<template>
  <div class="member-orders-board-container">
    <v-data-table
      :headers="memberOrdersHeaders"
      :items="tableData"
      :search="orderTableSearch"
      :items-per-page="-1"
      :loading="loading"
      item-key="orderNumber"
      :custom-filter="customFilter"
      :custom-sort="customSort"
      hide-default-footer
      :expanded.sync="expanded"
      fixed-header
    >
      <template #[`item.orderNumber`]="{ item }">
        <td @click="toggleRow(item)">
          <div class="ot-status-container">
            <span
              v-if="
                determineOrderStatusDot(item) !== 'error' &&
                determineOrderStatusDot(item) !== 'cancelled' &&
                determineOrderStatusDot(item) !== 'waiting'
              "
              :class="[determineOrderStatusDot(item), 'dot']"
            />
            <span v-else-if="determineOrderStatusDot(item) === 'error'" class="error-status">
              !
            </span>
            <span
              v-else-if="
                determineOrderStatusDot(item) === 'cancelled' ||
                determineOrderStatusDot(item) === 'waiting'
              "
              class="cancelled-status"
            />
            <span v-if="item.customer" :to="`/order/customer/${item.orderNumber}`">
              {{ item.orderNumber }}
            </span>
            <span v-else-if="item.corporation" :to="`/order/partner/${item.orderNumber}`">
              {{ item.orderNumber }}
            </span>
          </div>
        </td>
      </template>
      <template #[`item.status`]="{ item }">
        <td @click="toggleRow(item)">
          <span :class="[determineOrderStatus(item.status)]">
            {{ item.status }}
          </span>
        </td>
      </template>
      <template #[`item.orderDate`]="{ item }">
        <td @click="toggleRow(item)">
          {{ dateV2(item.orderDate, dateFormat) }}
        </td>
      </template>
      <template #[`item.voucherId`]="{ item }">
        <td>
          <div v-if="item.voucherId && item.voucherId > 0">Yes</div>
          <div v-else>No</div>
        </td>
      </template>
      <template #[`item.shipDate`]="{ item }">
        <td @click="toggleRow(item)">
          {{ dateV2(item.shipDate, dateFormat) }}
        </td>
      </template>
      <template #[`item.deliveryDate`]="{ item }">
        <td @click="toggleRow(item)">
          {{ dateV2(item.deliveryDate, dateFormat) }}
        </td>
      </template>
      <template #[`item.trackingNumber`]="{ item }">
        <td>
          <a v-if="item.trackingNumber" :href="createTrackingLink(item)" target="_blank">
            {{ item.trackingNumber }}
          </a>
          <span v-else> -- </span>
        </td>
      </template>
      <template #[`item.customer.name`]="{ item }">
        <td style="white-space: nowrap" @click="toggleRow(item)">
          <span v-if="item.customer">
            {{ `${item.customer.lastName}, ${item.customer.firstName}` }}<br />
            {{ `${item.customer.email}` }}
          </span>
        </td>
      </template>
      <template #[`item.campaignCode`]="{ item }">
        <td @click="toggleRow(item)">
          {{ item.campaignCode || '--' }}
        </td>
      </template>
      <template #[`item.technician`]="{ item }">
        <td @click="toggleRow(item)">
          <span v-if="item.customer.techFirstName && item.customer.techLastName">
            {{ item.customer.techFirstName }} {{ item.customer.techLastName }}
          </span>
          <span v-else>--</span>
        </td>
      </template>
      <template #expanded-item="{ item }">
        <td colspan="15" class="order-expand-container">
          <table>
            <thead>
              <tr>
                <td class="oet-header">Quantity</td>
                <td class="oet-header">SKU</td>
                <td class="oet-header">Description</td>
                <td class="oet-header">Status</td>
                <td class="oet-header">Ship Date</td>
                <td class="oet-header">Vendor</td>
                <td class="oet-header">Vendor Sku</td>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(o, idx) in getOrderLine(item)" :key="idx">
                <td>
                  {{ o.quantity }}
                </td>
                <td>
                  <div class="flex-row align-center">
                    <div
                      v-if="
                        determineOrderStatusDot(o) !== 'error' &&
                        determineOrderStatusDot(o) !== 'cancelled'
                      "
                      :class="[determineOrderStatusDot(o), 'dot']"
                    />
                    <div v-else-if="determineOrderStatusDot(o) === 'error'" class="error-status" />
                    <div
                      v-else-if="determineOrderStatusDot(o) === 'cancelled'"
                      class="cancelled-status"
                    />
                    <div>{{ o.sku }}</div>
                  </div>
                </td>
                <td>
                  {{ o.itemName }}
                </td>
                <td :class="[determineOrderStatus(o.status)]">
                  {{ o.status }}
                </td>
                <td v-if="o.shipDate">
                  {{ o.shipDate | date }}
                </td>
                <td v-else>--</td>
                <td>{{ o.vendor }}</td>
                <td>{{ o.vendorSku }}</td>
              </tr>
            </tbody>
          </table>
        </td>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import orderMixin from '@shared/mixins/orderMixin';
import unitPreferencesMixin from '@shared/mixins/unitPreferencesMixin';

export default {
  name: 'MemberOrdersBoard',
  filters: {
    name(obj) {
      let name = '';
      if (obj) {
        if (obj.firstName) {
          name = obj.firstName;
        }
        if (obj.lastName) {
          name = `${name} ${obj.lastName}`;
        }
        return name;
      }
      return '--';
    },
  },
  mixins: [unitPreferencesMixin, orderMixin],
  props: {
    orderTableSearch: String,
  },
  data() {
    return {
      expanded: [],
    };
  },
  computed: {
    tableData() {
      return this.orderTableData;
    },
  },
  methods: {
    toggleRow(item) {
      const index = this.expanded.findIndex((i) => i === item);
      if (index > -1) {
        this.expanded.splice(index, 1);
      } else {
        this.expanded.push(item);
      }
    },
    getOrderLine(data) {
      const newData = _.cloneDeep(data);
      if (this.activeTab === 'customer') {
        return newData.orderLine;
      }
      if (this.orderTableSearch) {
        const s = this.orderTableSearch.toLowerCase();
        return newData.orderLines.filter((ol) => {
          const firstName = ol.shippingAddress && ol.shippingAddress.firstName;
          const lastName = ol.shippingAddress && ol.shippingAddress.lastName;
          const name = `${firstName} ${lastName}`;
          const email = ol.shippingAddress && ol.shippingAddress.email;
          const phone = ol.shippingAddress && ol.shippingAddress.phone;
          return (
            (name && name.toLowerCase().match(s)) ||
            (email && email.toLowerCase().match(s)) ||
            (phone && phone.toLowerCase().match(s))
          );
        });
      }
      return newData.orderLines;
    },
    customSort(items, index, isDesc) {
      this.$store.dispatch('changeOrderTableOptions', [
        {
          field: 'sortBy',
          value: index,
        },
        {
          field: 'sortDesc',
          value: isDesc,
        },
      ]);

      items.sort((a, b) => {
        if (index[0] === 'customer.name') {
          if (!a.customer.firstName && !a.customer.lastName) {
            return 1;
          }
          if (!b.customer.firstName && !b.customer.lasName) {
            return -1;
          }
          if (!isDesc[0]) {
            return `${a.customer.firstName.toLowerCase()} ${a.customer.lastName.toLowerCase()}` <
              `${b.customer.firstName.toLowerCase()} ${b.customer.lastName.toLowerCase()}`
              ? -1
              : 1;
          }
          return `${b.customer.firstName.toLowerCase()} ${b.customer.lastName.toLowerCase()}` <
            `${a.customer.firstName.toLowerCase()} ${a.customer.lastName.toLowerCase()}`
            ? -1
            : 1;
        }
        if (index[0] === 'status') {
          if (!a.status) {
            return 1;
          }
          if (!b.status) {
            return -1;
          }
          if (!isDesc[0]) {
            return a.status < b.status ? -1 : 1;
          }
          return b.status < a.status ? -1 : 1;
        }
        if (index[0] === 'orderDate') {
          if (!a.orderDate) {
            return 1;
          }
          if (!b.orderDate) {
            return -1;
          }
          const dateA = new Date(a.orderDate);
          const dateB = new Date(b.orderDate);

          if (!isDesc[0]) {
            return dateA < dateB ? -1 : 1;
          }
          return dateB < dateA ? -1 : 1;
        }
        if (index[0] === 'shipDate') {
          if (!a.shipDate) {
            return 1;
          }
          if (!b.shipDate) {
            return -1;
          }
          const dateA = new Date(a.shipDate);
          const dateB = new Date(b.shipDate);

          if (!isDesc[0]) {
            return dateA < dateB ? -1 : 1;
          }
          return dateB < dateA ? -1 : 1;
        }
        if (index[0] === 'deliveryDate') {
          if (!a.deliveryDate) {
            return 1;
          }
          if (!b.deliveryDate) {
            return -1;
          }
          const dateA = new Date(a.deliveryDate);
          const dateB = new Date(b.deliveryDate);

          if (!isDesc[0]) {
            return dateA < dateB ? -1 : 1;
          }
          return dateB < dateA ? -1 : 1;
        }
        if (index[0] === 'orderNumber') {
          const orderNumberA = Number(a.orderNumber);
          const orderNumberB = Number(b.orderNumber);
          if (!isDesc[0]) {
            return orderNumberA < orderNumberB ? -1 : 1;
          }
          return orderNumberB < orderNumberA ? -1 : 1;
        }
        if (index[0] === 'trackingNumber') {
          if (!a.trackingNumber) {
            return 1;
          }
          if (!b.trackingNumber) {
            return -1;
          }
          if (!isDesc[0]) {
            return a.trackingNumber < b.trackingNumber ? -1 : 1;
          }
          return b.trackingNumber < a.trackingNumber ? -1 : 1;
        }
        if (index[0] === 'technician') {
          if (!a.customer?.techFirstName) {
            return 1;
          }
          if (!b.customer?.techFirstName) {
            return -1;
          }
          if (!isDesc[0]) {
            return `${a.customer.techFirstName.toLowerCase()} ${a.customer.techLastName.toLowerCase()}` <
              `${b.customer.techFirstName.toLowerCase()} ${b.customer.techLastName.toLowerCase()}`
              ? -1
              : 1;
          }
          return `${b.customer.techFirstName.toLowerCase()} ${b.customer.techLastName.toLowerCase()}` <
            `${a.customer.techFirstName.toLowerCase()} ${a.customer.techLastName.toLowerCase()}`
            ? -1
            : 1;
        }
        return 0;
      });

      return items;
    },
    customFilter(value, search, item) {
      const { orderDevices, orderLine } = item;
      const od = orderDevices ? orderDevices.map((o) => o.uid).join(' ') : null;
      const oNames = [];
      const emails = [];
      const phones = [];
      orderLine.forEach((ol) => {
        const name = `${ol.shippingAddress.firstName} ${ol.shippingAddress.lastName}`;
        oNames.push(name);
        emails.push(ol.shippingAddress.email);
        phones.push(ol.shippingAddress.phone);
      });

      const techName = `${item.customer.techFirstName} ${item.customer.techLastName}`;

      return (
        value != null &&
        search != null &&
        (value.toString().toLowerCase().indexOf(search.toLowerCase()) !== -1 ||
          (od && od.toString().toLowerCase().indexOf(search.toLowerCase()) !== -1) ||
          oNames.toString().toLowerCase().indexOf(search.toLowerCase()) !== -1 ||
          emails.toString().toLowerCase().indexOf(search.toLowerCase()) !== -1 ||
          phones.toString().toLowerCase().indexOf(search.toLowerCase()) !== -1 ||
          techName.toString().toLowerCase().indexOf(search.toLowerCase()) !== -1)
      );
    },
  },
};
</script>

<style lang="scss">
.member-orders-board-container {
  width: 100%;
  margin-top: 34px;
  padding: 0 0 12px 8px;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  position: relative;
  background-color: $N95;
  .ot-status-container {
    display: flex;
    align-items: center;
  }

  .order-expand-container {
    padding: 0 !important;
    table {
      width: 100%;
      padding: 0 8px;
      thead {
        .oet-header {
          padding: 6px;
          background-color: $CONTENTGRAY !important;
          font-weight: 600;
          text-transform: uppercase;
          color: white;
          text-align: left;
        }
      }
      tbody {
        td {
          text-align: left;
          padding: 0 6px !important;
        }
      }
    }

    .green-dot {
      background-color: $BASIL100;
      width: 8px;
      height: 8px;
      border-radius: 4px;
      margin-right: 12px;
    }

    .red-dot {
      background-color: $TOMATO100;
      width: 12px;
      height: 8px;
      border-radius: 4px;
      margin-right: 12px;
    }

    .yellow-dot {
      background-color: $SUNSET100;
      width: 8px;
      height: 8px;
      border-radius: 4px;
      margin-right: 12px;
    }

    .error-status {
      width: 8px;
      margin-right: 12px;
      text-align: center;
      color: $TOMATO100;
      font-weight: 700;
      font-size: 1rem;
      transform: scale(1.2);
    }

    .cancelled-status {
      width: 8px;
      margin-right: 12px;
      text-align: center;
      color: $BLACK;
      font-weight: 700;
      font-size: 1rem;
      transform: scale(1.2);
    }
  }

  .v-data-table__wrapper {
    background-color: $N95;
    height: 700px;

    table {
      border-collapse: separate !important;
      border-spacing: 0 8px !important;

      thead {
        th {
          height: 30px !important;
          background: $N95 !important;

          span {
            color: $WHITE;
          }

          .v-icon {
            color: $WHITE !important;
          }

          &:last-child {
            text-align: end !important;
          }
        }

        .v-data-table__progress {
          th {
            height: 1px !important;
          }
        }
      }

      tbody {
        tr {
          cursor: pointer;

          td {
            padding: 20px;
            background: $N90 !important;
            color: $WHITE;
            font-weight: 600;

            &:first-child {
              border-top-left-radius: 8px;
              border-bottom-left-radius: 8px;
            }

            &:last-child {
              border-top-right-radius: 8px;
              border-bottom-right-radius: 8px;
              text-align: right;
              white-space: nowrap;
            }

            &:only-child {
              text-align: center;
            }

            a {
              color: $WHITE;
              text-decoration: underline;
            }
          }
        }
      }

      .verify-badge {
        width: 24px;
        height: 24px;
      }

      .verification-badge-container {
        position: relative;
        cursor: pointer;

        &:hover {
          .tooltip {
            display: flex;
            flex-direction: column;
          }

          .tooltip-unverified {
            display: flex;
            flex-direction: column;
          }

          .tooltip-img {
            display: flex;
          }
        }

        .pending-verification {
          font-size: 14px;
          font-weight: 600;
          color: $WHITE;
        }
      }

      .tooltip-unverified {
        position: absolute;
        padding: 10px;
        width: 316px;
        background-color: $WHITE;
        color: $BLACK;
        border-radius: 8px;
        align-items: left;
        justify-content: center;
        border-radius: 4px;
        box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25);
        padding: 10px 10px;
        z-index: 1;
        top: 40px;
        right: 15px;
        display: none;

        &:after {
          content: '';
          position: absolute;
          top: 0;
          left: 50%;
          width: 0;
          height: 0;
          border: 8px solid transparent;
          border-bottom-color: $WHITE;
          border-top: 0;
          margin-left: -8px;
          margin-top: -8px;
        }
      }

      .tooltip-unverified-pill-container {
        display: flex;
        flex-direction: row;
        flex-wrap: wrap;
        height: 100%;

        .tooltip-unverified-pill {
          padding: 5px;
          border-radius: 25px;
          margin-right: 5px;
          background-color: $SUNSET100;
          margin-bottom: 10px;
        }
      }

      .first-reason-container {
        display: flex;
        flex-direction: row;
        align-items: center;
        width: 200px;
      }

      .first-reason-pill {
        padding: 5px;
        border-radius: 25px;
        margin-right: 10px;
        background-color: $SUNSET100;
        color: $BLACK;
        // margin-bottom: 10px;
      }

      .more-reasons-indicator {
        // padding: 5px;
        display: flex;
        justify-content: center;
        align-items: center;
        background-color: $WHITE;
        border-radius: 15px;
        color: $BLACK;
        font-size: 12px;
        font-weight: 500;
        // margin-right: 5px;
        height: 25px;
        width: 25px;
      }

      .tooltip {
        position: absolute;
        width: 350px;
        background-color: $SUNSET100;
        color: $BLACK;
        align-items: left;
        justify-content: center;
        border-radius: 4px;
        box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25);
        padding: 10px 10px;
        z-index: 1;
        top: 28px;
        left: -40px;
        display: none;
      }

      .tooltip-img {
        position: absolute;
        width: 320px;
        height: 356px;
        border-radius: 4px;
        box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25);
        padding: 10px 10px;
        z-index: 1;
        top: 28px;
        left: -40px;
        display: none;
      }
    }
  }
}
</style>
